import { OZ_DARK } from 'HubStyleTokens/colors';
import FormattedMessage from 'I18n/components/FormattedMessage';
import UIClickable from 'UIComponents/button/UIClickable';
import UIOptimisticNanoProgress from 'UIComponents/progress/UIOptimisticNanoProgress';
import { createActionCardCounters, getBranchIndex, getEdgeIdFromGoToActionId, getPreviousActionId, isGoToActionId } from 'automation-ui-action-features/records/recordsUtils';
import BranchName from 'automation-ui-canvas/components/canvas/BranchName';
import FlowCanvasNode from 'automation-ui-canvas/components/canvas/FlowCanvasNode';
import BranchLabel from 'automation-ui-canvas/components/canvas/BranchLabel';
import { MessageComponent } from 'automation-ui-canvas/containers/enrollment-conversion/EnrollmentConversionMessage';
import ModeIndicator from 'automation-ui-canvas/containers/enrollment-history/utils/ModeIndicator';
import MultiRevisionEmptyState from 'automation-ui-canvas/containers/enrollment-history/utils/MultiRevisionEmptyState';
import { getExecutionContextMap, getFollowedBranchIndexForAction, isBranchAction, isWorkflowUpdatedInCurrentExecution } from 'automation-ui-canvas/containers/enrollment-history/utils/executionContextUtils';
import { getEnrolledObjectLabel } from 'automation-ui-canvas/containers/enrollment-history/utils/getEnrolledObjectLabel';
import { wireEnrollmentHistoryPropsFromURL } from 'automation-ui-canvas/containers/enrollment-history/wireEnrollmentHistoryPropsFromURL';
import { wireFlowByEnrollmentTimestamp } from 'automation-ui-canvas/containers/enrollment-history/wireFlowByEnrollmentTimestamp';
import { wireGetEnrollmentDetails } from 'automation-ui-canvas/containers/enrollment-history/wireGetEnrollmentDetails';
import PannableDecorator from 'automation-ui-canvas/decorators/PannableDecorator';
import ZoomableDecorator from 'automation-ui-canvas/decorators/ZoomableDecorator';
import { getActionOutputsForTargetActionById } from 'automation-ui-canvas/flow/records/Flow/flowUtils';
import { trackEvent } from 'automation-ui-canvas/utils/trackingUtils';
import { getEnrollmentHistoryUrl } from 'automation-ui-canvas/utils/urls';
import { wireAuthConsumer } from 'automation-ui-core/context/AuthContext';
import { attachWrappedComponent } from 'automation-ui-core/decoratorUtils';
import * as SpecialActionIds from 'automation-ui-flow-core/canvas/SpecialActionIds';
import { LogEventTypes } from 'automation-ui-log-events/constants/LogEventConstants';
import { getActionType, getIdempotencyId, getType } from 'automation-ui-log-events/records/LogEvent/utils';
import { getFlowEditRoute } from 'automation-ui-routing/utils';
import Trackables from 'automation-ui-usage-tracking/Trackables';
import isEmpty from 'hs-lodash/isEmpty';
import { Component, createRef } from 'react';
import { NavMarker } from 'react-rhumb';
import { withRouter } from 'react-router-dom';
import getComponentName from 'react-utils/getComponentName';
import { compose } from 'redux';
import styled from 'styled-components';
import EndMarker from 'automation-ui-canvas/components/common/EndMarker';
import BranchDescription from 'automation-ui-canvas/components/canvas/BranchDescription';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const StyledCanvas = styled.div.withConfig({
  displayName: "FlowCanvasSelectedEnrollmentDecorator__StyledCanvas",
  componentId: "bt7opa-0"
})(["height:100%;"]);
const StyledUIOptimisticNanoProgress = styled(UIOptimisticNanoProgress).withConfig({
  displayName: "FlowCanvasSelectedEnrollmentDecorator__StyledUIOptimisticNanoProgress",
  componentId: "bt7opa-1"
})(["position:absolute;top:0;left:0;right:0;"]);
function getComposedCanvas() {
  return PannableDecorator(
  // @ts-expect-error ZoomableDecorator expects StyledCanvas to have its required props, but it's just a div
  // and the required props aren't wired until ComposedCanvas is used in FlowCanvasBase. Not sure if real error or not
  ZoomableDecorator({
    WrappedComponent: StyledCanvas
  }));
}
export default function FlowCanvasSelectedEnrollmentDecorator(WrappedComponent, centerOnMount) {
  const componentName = getComponentName(WrappedComponent);
  class FlowCanvasSelectedEnrollment extends Component {
    constructor(...args) {
      super(...args);
      this.isNodeHighlighted = nodeId => {
        const executionPathNodeIds = this.state.executionPathNodeIds;
        return !!executionPathNodeIds && executionPathNodeIds.has(nodeId);
      };
      this.handleGetActionOutputsForTargetActionById = (targetActionId, isNew) => {
        const {
          flow,
          allOutputsByActionTypeId,
          instanceOutputsByActionId
        } = this.props;
        return getActionOutputsForTargetActionById({
          flow: flow,
          allOutputsByActionTypeId,
          instanceOutputsByActionId,
          targetActionId,
          isNewAction: isNew
        });
      };
      this.isEdgeHighlighted = edgeId => {
        const {
          executionPathNodeIds
        } = this.state;
        const {
          logEvents
        } = this.props;
        const prevActionId = getPreviousActionId(edgeId);
        // Need additional check for branch edge to only highlight the edge if the branch is met
        const isPrevActionBranch = isBranchAction(prevActionId, logEvents);
        return isPrevActionBranch && this.isMetBranch(edgeId) || !isPrevActionBranch && executionPathNodeIds.has(prevActionId);
      };
      this.onRenderCanvasNode = ({
        id,
        width,
        height
      }) => {
        const {
          flow,
          isMulti,
          logEvents,
          logEventKey
        } = this.props;
        if (!flow) {
          return null;
        }
        const {
          executionCardContextMap
        } = this.state;
        const executionCardContext = executionCardContextMap.get(id);
        let borderStyle = executionCardContext && executionCardContext.status;
        const isSelected = executionCardContext && executionCardContext.logEventKey === logEventKey;
        let bannerText = null;
        let bannerStyle = undefined;
        if (id === SpecialActionIds.ENROLLMENT) {
          const enrollmentLogEvent = logEvents.find(logEvent => logEvent.type === LogEventTypes.ENROLLMENT);
          bannerText = getEnrolledObjectLabel(enrollmentLogEvent);
          bannerStyle = executionCardContext && executionCardContext.status;
        }
        if (isGoToActionId(id)) {
          const edgeId = getEdgeIdFromGoToActionId(id);
          const prevActionId = getPreviousActionId(edgeId);
          const prevActionExecutionContext = executionCardContextMap.get(prevActionId);
          if (this.isEdgeHighlighted(edgeId) && prevActionExecutionContext) {
            borderStyle = prevActionExecutionContext.status;
          }
        }
        return /*#__PURE__*/_jsx(FlowCanvasNode, {
          id: id,
          width: width,
          height: height,
          isSelected: isSelected,
          clickable: !!executionCardContext,
          flow: flow,
          isHighlighted: this.isNodeHighlighted(id),
          readOnly: true,
          showIcon: !isMulti,
          onSelectNode: this.onSelectAction,
          getActionOutputsForTargetActionById: this.handleGetActionOutputsForTargetActionById,
          borderStyle: borderStyle,
          bannerText: bannerText,
          bannerStyle: bannerStyle
        });
      };
      this.isMetBranch = edgeId => {
        const {
          logEvents
        } = this.props;
        const branchIndex = getBranchIndex(edgeId);
        const branchActionId = getPreviousActionId(edgeId);
        return branchIndex === getFollowedBranchIndexForAction(branchActionId, logEvents);
      };
      this.onRenderBranchName = edgeId => {
        const {
          flow
        } = this.props;
        return /*#__PURE__*/_jsx(BranchLabel, {
          branchLabelHeaderText: /*#__PURE__*/_jsx(BranchName, {
            flow: flow,
            edgeId: edgeId,
            handleGetActionOutputsForTargetActionById: this.handleGetActionOutputsForTargetActionById
          }),
          branchDescription: /*#__PURE__*/_jsx(BranchDescription, {
            flow: flow,
            edgeId: edgeId
          }),
          hideFooter: true,
          borderStyle: this.isMetBranch(edgeId) ? 'success' : undefined
        });
      };
      this.updateURLWithLogEventKey = logEventKey => {
        const {
          history,
          flowId
        } = this.props;
        const searchParams = new URLSearchParams(history.location.search);
        searchParams.set('logEventKey', logEventKey);
        history.replace({
          pathname: getEnrollmentHistoryUrl({
            flowId
          }),
          search: searchParams.toString()
        });
      };
      this.onRenderEndMarker = ({
        edgeId
      }) => {
        const {
          flow
        } = this.props;
        const {
          executionCardContextMap
        } = this.state;
        const executionCardContext = executionCardContextMap.get('unenrollment');
        let borderStyle;
        const shouldHighlightEndMarker = this.isEdgeHighlighted(edgeId);
        if (shouldHighlightEndMarker) {
          borderStyle = executionCardContext && executionCardContext.status;
        }
        return /*#__PURE__*/_jsx(UIClickable, {
          onClick: () => {
            trackEvent(Trackables.PERFORMANCE_HISTORY_CLICK_WORKFLOW_NODE, {
              logEventType: LogEventTypes.UNENROLLMENT,
              subscreen: 'selected-enrollment'
            });
            this.updateURLWithLogEventKey(executionCardContext.logEventKey);
          },
          disabled: !shouldHighlightEndMarker,
          children: /*#__PURE__*/_jsx(EndMarker, {
            flow: flow,
            edgeId: edgeId,
            borderStyle: borderStyle,
            hideFooter: true
          })
        });
      };
      this.getComposedProps = () => {
        const {
          flow,
          canvasScrollElementRef
        } = this.props;
        return {
          flow: flow,
          canvasScrollElementRef,
          onSelectAction: this.onSelectAction,
          ComposedCanvas: this.ComposedCanvas,
          readOnlyOverride: true,
          // Used for permission overrides like revisions and enrollment history
          centerOnMount,
          highlightedActionIds: this.state.executionPathNodeIds,
          TopContent: this.renderTopContent(),
          onRenderCanvasNode: this.onRenderCanvasNode,
          onRenderBranchName: this.onRenderBranchName,
          onRenderEndMarker: this.onRenderEndMarker,
          highlightedEdgeThickness: 4,
          highlightedEdgeColor: OZ_DARK,
          getIsBranchEdgeHighlighted: this.isMetBranch
        };
      };
      this.onSelectAction = id => {
        const {
          executionCardContextMap
        } = this.state;
        const {
          logEvents
        } = this.props;
        const executionCardContext = executionCardContextMap.get(id);
        const logEventKey = executionCardContext.logEventKey;
        const currentLogEvent = logEvents.find(logEvent => getIdempotencyId(logEvent) === logEventKey);
        trackEvent(Trackables.PERFORMANCE_HISTORY_CLICK_WORKFLOW_NODE, {
          subscreen: 'selected-enrollment',
          logEventType: getType(currentLogEvent),
          actionType: getActionType(currentLogEvent)
        });
        this.updateURLWithLogEventKey(logEventKey);
      };
      this.renderTopContent = () => {
        return /*#__PURE__*/_jsx(MessageComponent, {
          title: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "automation.enrollmentHistory.messages.enrollmentPath.text"
          }),
          tooltipContent: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "automation.enrollmentHistory.messages.enrollmentPath.tooltip"
          })
        });
      };
      this.ComposedCanvas = getComposedCanvas();
      this.headerRef = /*#__PURE__*/createRef();
      this.state = {
        executionPathNodeIds: new Set(),
        executionCardContextMap: null,
        actionCardCountersMap: {}
      };
    }
    static getDerivedStateFromProps(props, state) {
      const {
        flow
      } = props;
      const {
        actionCardCountersMap
      } = state;
      if (!flow) {
        return null;
      }
      const countersMap = isEmpty(actionCardCountersMap) ? createActionCardCounters(flow.actions, flow.getFirstActionId()) : actionCardCountersMap;
      return {
        actionCardCountersMap: countersMap
      };
    }
    componentDidUpdate(prevProps) {
      const {
        logEvents,
        areLogEventsLoading
      } = this.props;
      if ((prevProps.logEvents !== logEvents || !this.state.executionCardContextMap) && !areLogEventsLoading) {
        const executionCardContextMap = getExecutionContextMap(logEvents);
        // Removing unenrollment as it is not a part of special action
        // ids
        const executionPathNodeIds = new Set(executionCardContextMap.keySeq().toSet().remove('unenrollment').toArray());
        this.setState({
          executionPathNodeIds,
          executionCardContextMap
        });
      }
    }
    renderContent() {
      const {
        flow
      } = this.props;
      const isLoading = !flow || !this.state.executionCardContextMap;
      if (isLoading) {
        return /*#__PURE__*/_jsx(StyledUIOptimisticNanoProgress, {
          value: 10
        });
      }
      if (isWorkflowUpdatedInCurrentExecution(flow, this.state.executionCardContextMap)) {
        return /*#__PURE__*/_jsx(MultiRevisionEmptyState, {
          objectTypeId: flow.objectTypeId
        });
      }

      // Now we do have access to logEvents and the flow

      return /*#__PURE__*/_jsx(WrappedComponent, Object.assign({}, this.props, this.getComposedProps()));
    }
    render() {
      const {
        history,
        flowId
      } = this.props;
      return /*#__PURE__*/_jsxs(NavMarker, {
        name: "ENROLLMENT_HISTORY_SELECTED_ENROLLMENT",
        children: [/*#__PURE__*/_jsx(ModeIndicator, {
          onCancel: () => {
            trackEvent(Trackables.PERFORMANCE_HISTORY_CLOSE_MODE_INDICATOR);
            history.push(getFlowEditRoute(flowId));
          },
          dataTestId: "enrollment-history-mode-indicator",
          modeTitle: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "automation.enrollmentHistory.mode.title"
          })
        }), this.renderContent()]
      });
    }
  }
  const ConnectedFlowCanvasSelectedEnrollment = compose(wireAuthConsumer, withRouter, wireEnrollmentHistoryPropsFromURL, wireGetEnrollmentDetails, wireFlowByEnrollmentTimestamp)(FlowCanvasSelectedEnrollment);
  ConnectedFlowCanvasSelectedEnrollment.displayName = `ConnectedFlowCanvasSelectedEnrollment(${componentName})`;
  attachWrappedComponent(ConnectedFlowCanvasSelectedEnrollment, WrappedComponent);
  return ConnectedFlowCanvasSelectedEnrollment;
}